import React, {
  createContext,
  useContext,
  useMemo,
  useReducer,
  useState,
} from 'react';
import useLogEventInAnalytics from '../../hooks/useLogAnalyticsEvent';
import { CustomEvents } from '../../constants/analytics.constant';
import {
  ASSISTANCE_TYPE_GAS,
  ASSISTANCE_TYPE_LOCKSMITH,
  ASSISTANCE_TYPE_TOWING,
  AssistanceDetails,
} from '../../constants/assistance.constant';
import {
  assistanceStatusAPI,
  cancelAssistanceAPI,
  createAssistanceAPI,
  currentAssistance,
  getAllAssistances,
} from '../../services/assistances/assistances.service';
import AssistanceReducer, {
  FAILURE_ASSISTANCE,
  FAILURE_ASSISTANCES,
  FAILURE_ASSISTANCE_STATUS,
  FAILURE_CANCEL_ASSISTANCE,
  FAILURE_CURRENT_ASSISTANCE,
  REQUEST_ASSISTANCE,
  REQUEST_ASSISTANCES,
  REQUEST_ASSISTANCE_STATUS,
  REQUEST_CANCEL_ASSISTANCE,
  REQUEST_CURRENT_ASSISTANCE,
  SUCCESS_ASSISTANCE,
  SUCCESS_ASSISTANCES,
  SUCCESS_ASSISTANCE_STATUS,
  SUCCESS_CANCEL_ASSISTANCE,
  SUCCESS_CURRENT_ASSISTANCE,
  UPDATE_ASSISTANCE_BODY,
} from './assistance.reducer';

export const AssistanceDataContext = createContext<any>(null);

const initialValues = {
  success: {},
  errors: {},
  assistance_body: {},
  assistances: [],
  loading: false,
};

const AssistanceDataProvider = ({ ...props }) => {
  const [step, setStep] = useState(0);
  const [state, dispatch] = useReducer(AssistanceReducer, initialValues);
  const [open, setOpen] = useState(false);
  const { logEvent } = useLogEventInAnalytics();

  const nextStep = (value?: any) => {
    const { assistance_body } = state;
    const { assistance_detail } = assistance_body;

    switch (step) {
      case 0:
        setStep(step + 1);
        break;

      case 1:
        // REMOLQUE
        if (
          value === ASSISTANCE_TYPE_TOWING ||
          value === ASSISTANCE_TYPE_GAS ||
          value === ASSISTANCE_TYPE_LOCKSMITH
        ) {
          setStep(3);
        } else {
          setStep(step + 1);
        }
        break;

      case 2:
        // REMOLQUE
        if (
          value === AssistanceDetails.VEHICLE_WONT_START ||
          value === AssistanceDetails.SPARE_TIRE
        ) {
          setStep(4);
        } else {
          setStep(step + 1);
        }
        break;

      case 4:
        // MECANICA
        if (
          assistance_detail === AssistanceDetails.VEHICLE_WONT_START ||
          assistance_detail === AssistanceDetails.SPARE_TIRE
        ) {
          setStep(7);
        } else {
          setStep(step + 1);
        }
        break;

      default:
        setStep(step + 1);
        break;
    }
  };

  const goBack = () => {
    const { assistance_body } = state;
    const { assistance_type, assistance_detail } = assistance_body;

    switch (step) {
      case 3:
        // REMOLQUE
        if (
          assistance_type === ASSISTANCE_TYPE_TOWING ||
          assistance_type === ASSISTANCE_TYPE_GAS ||
          assistance_type === ASSISTANCE_TYPE_LOCKSMITH
        ) {
          setStep(1);
        } else {
          setStep(step - 1);
        }
        break;

      case 4:
        // MECANICA
        if (
          assistance_detail === AssistanceDetails.VEHICLE_WONT_START ||
          assistance_detail === AssistanceDetails.SPARE_TIRE
        ) {
          setStep(2);
        } else {
          setStep(step - 1);
        }
        break;

      case 7:
        // MECANICA
        if (
          assistance_detail === AssistanceDetails.VEHICLE_WONT_START ||
          assistance_detail === AssistanceDetails.SPARE_TIRE
        ) {
          setStep(4);
        } else {
          setStep(step - 1);
        }
        break;

      default:
        setStep(step - 1);
        break;
    }
  };

  const updateAssistanceBody = (body: any) => {
    dispatch({
      type: UPDATE_ASSISTANCE_BODY,
      payload: body,
    });
  };

  const cleanAssistanceBody = () => {
    dispatch({
      type: UPDATE_ASSISTANCE_BODY,
    });
  };

  const getAssistances = async () => {
    dispatch({
      type: REQUEST_ASSISTANCES,
      // payload: state.assistance_bod,
    });
    try {
      const res = await getAllAssistances();
      dispatch({
        type: SUCCESS_ASSISTANCES,
        payload: res.data,
      });
    } catch (err) {
      dispatch({
        type: FAILURE_ASSISTANCES,
        payload: err.response?.data,
      });
    }
  };

  const createAssistance = async () => {
    dispatch({
      type: REQUEST_ASSISTANCE,
      // payload: state.assistance_bod,
    });
    try {
      const res = await createAssistanceAPI(state.assistance_body);

      await logEvent(CustomEvents.ASSISTANCE_REQUESTED, { app: process.env.REACT_APP_WEBSITE })
      dispatch({
        type: SUCCESS_ASSISTANCE,
        payload: res.data,
      });
      setStep(0);
    } catch (err) {
      dispatch({
        type: FAILURE_ASSISTANCE,
        payload: err.response?.data,
      });
    }
  };

  const checkAssistanceStatus = async (id: any) => {
    dispatch({
      type: REQUEST_ASSISTANCE_STATUS,
      // payload: state.assistance_bod,
    });
    try {
      const res = await assistanceStatusAPI(id);
      dispatch({
        type: SUCCESS_ASSISTANCE_STATUS,
        payload: res.data,
      });
    } catch (err) {
      dispatch({
        type: FAILURE_ASSISTANCE_STATUS,
        payload: err.response?.data,
      });
    }
  };

  const checkCurrentAssistance = async () => {
    dispatch({
      type: REQUEST_CURRENT_ASSISTANCE,
      // payload: state.assistance_bod,
    });
    try {
      const res = await currentAssistance();
      dispatch({
        type: SUCCESS_CURRENT_ASSISTANCE,
        payload: res.data,
      });
    } catch (err) {
      dispatch({
        type: FAILURE_CURRENT_ASSISTANCE,
        payload: err.response?.data,
      });
    }
  };

  const cancelAssistance = async (id: any) => {
    dispatch({
      type: REQUEST_CANCEL_ASSISTANCE,
      // payload: state.assistance_bod,
    });
    try {
      const res = await cancelAssistanceAPI(id);
      dispatch({
        type: SUCCESS_CANCEL_ASSISTANCE,
        payload: res.data,
      });
      await logEvent(CustomEvents.ASSISTANCE_CANCEL, {
        category: 'accion',
        action: 'assistance_cancel',
      });
    } catch (err) {
      dispatch({
        type: FAILURE_CANCEL_ASSISTANCE,
        payload: err.response?.data,
      });
    }
  };

  const assitanceDataValue = useMemo(
    () => ({
      ...state,
      open,
      setOpen,
      step,
      setStep,
      goBack,
      nextStep,
      updateAssistanceBody,
      cleanAssistanceBody,
      getAssistances,
      createAssistance,
      cancelAssistance,
      checkAssistanceStatus,
      checkCurrentAssistance,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [step, open, state]
  );

  return (
    <AssistanceDataContext.Provider value={assitanceDataValue} {...props} />
  );
};

export const useAssistanceDataContext = () => useContext(AssistanceDataContext);

export default AssistanceDataProvider;
